import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useGetExerciseById } from '_api';
import { Exercise } from '_api/types';
import { ExerciseRouteMap } from '_constants';
import {
  StyledDetailDescription,
  StyledDetailNotes,
  ThumbnailView,
} from 'components/atoms';
import { AddExerciseToPatientModal } from 'components/organisms/modals';
import React, { useEffect, useState } from 'react';
import { FaEdit, FaPlus } from 'react-icons/fa';
import { IoMdArrowBack } from 'react-icons/io';
import { getVideoAspectRatio, useNavigateTo } from 'utils';

import { DetailViewProps } from './types';

export const ExerciseDetail = ({ id }: DetailViewProps) => {
  const navigate = useNavigateTo();
  const theme = useTheme();
  const { data: exercise, isFetching: isLoading } = useGetExerciseById(id);
  const [isVertical, setIsVertical] = useState(false);
  const [exerciseToAdd, setExerciseToAdd] = useState<Exercise | undefined>(undefined);

  useEffect(() => {
    if (exercise && exercise.fullVideoUrl) {
      getVideoAspectRatio(exercise.fullVideoUrl, (ratio) => {
        if (ratio < 1) {
          setIsVertical(true);
        } else {
          setIsVertical(false);
        }
      });
    }
  }, [exercise]);

  if (!exercise && !isLoading) {
    return <Typography variant="h6">Exercise not found</Typography>;
  }

  const handleBack = () => {
    history.back();
  };

  const handleOnEdit = () => {
    navigate(ExerciseRouteMap.EDIT, id.toString());
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', paddingTop: '50px' }}>
      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        <Box sx={{ marginTop: '10px', color: `${theme.palette.primary.main}` }}>
          <IoMdArrowBack />
        </Box>
        <Button onClick={handleBack} sx={{ justifyContent: 'left' }}>
          {' '}
          Back{' '}
        </Button>
      </Box>
      {exercise && !isLoading ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant={'h3'} gutterBottom>
              {exercise.title}
            </Typography>
            <Tooltip title={'Edit'} placement="top">
              <span>
                <IconButton
                  onClick={handleOnEdit}
                  sx={{
                    color: theme.palette.primary.main,
                    paddingLeft: '10px',
                    marginBottom: '20px',
                  }}
                >
                  <FaEdit />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={'Add to patient'} placement="top">
              <span>
                <IconButton
                  aria-label="Add to patient"
                  onClick={() => setExerciseToAdd(exercise)}
                  sx={{
                    color: theme.palette.primary.main,
                    paddingLeft: '10px',
                    marginBottom: '20px',
                  }}
                >
                  <FaPlus />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              flexDirection: isVertical ? 'row' : 'column',
            }}
          >
            <ThumbnailView
              fullImageUrl={exercise.fullImageUrl}
              placeholder={true}
              fullVideoUrl={exercise.fullVideoUrl}
            />
            <Box
              sx={{
                paddingTop: isVertical ? '' : '20px',
                paddingLeft: isVertical ? '20px' : '',
              }}
            >
              <h3>Notes for Therapists</h3>
              <StyledDetailNotes>{exercise.notes}</StyledDetailNotes>
              <h3>Instructions</h3>
              <StyledDetailDescription variant="body1" sx={{ marginBottom: '16px' }}>
                {exercise.description}
              </StyledDetailDescription>
              {exercise.instructionList.map((inst, i) => (
                <StyledDetailDescription key={`${id}-instruction-${i}`}>
                  &bull; {inst}
                </StyledDetailDescription>
              ))}
            </Box>
          </Box>
        </>
      ) : (
        <Box sx={{ margin: 'auto' }}>
          <CircularProgress size={'5rem'} />
        </Box>
      )}
      <AddExerciseToPatientModal
        content={exerciseToAdd as Exercise}
        open={!!exerciseToAdd}
        onClose={() => setExerciseToAdd(undefined)}
        id={id}
      />
    </Box>
  );
};
